import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import withRouter from "../../withRouter";
import Header from "../../Component/Header";
import Menu from "../../Component/Menu";
import Footer from "../../Component/Footer";
import { currentTenant, userid } from "../../Common/Credentials";
import { spin } from "../../Common/NewLoader";
import { productionid } from "../../Common/Permissions";
import ProductionServices from "../ProductionServices";
import IOSSwitch from "../../CommonComponents/IOSSwitch";

const ProductionTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState({});
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshData = useCallback(() => {
    setLoading(true);
    ProductionServices.getallproductiontemplates(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => b.id - a.id);
        setTemplates(sortedData);
        setFilteredTemplates(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshData();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshData]);

  useEffect(() => {
    updateFilteredData();
  }, [templates, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = templates;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = templates.filter(item =>
        item.id?.toString().toLowerCase().includes(lowerSearchTerm) ||
        item.templatename?.toLowerCase().includes(lowerSearchTerm) ||
        item.category?.toLowerCase().includes(lowerSearchTerm) ||
        item.itemname?.toLowerCase().includes(lowerSearchTerm) ||
        item.uom?.toLowerCase().includes(lowerSearchTerm) ||
        item.status?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredTemplates(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredTemplates);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleActiveStatusChange = (template) => {
    const data = {
      itemid: template.id,
      userid: userid,
    };
    ProductionServices.UpdateTemplateStatus(data)
      .then(() => refreshData());
  };

  const handleDelete = (template) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete template "${template.templatename}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#152f5f',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        ProductionServices.DeleteTemplate(template.id)
          .then(() => {
            refreshData();
            Swal.fire(
              'Deleted!',
              'Template has been deleted.',
              'success'
            );
          });
      }
    });
  };

  const toggleExpandRow = (templateId) => {
    setExpandedRows(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredTemplates.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredTemplates.length)} of {filteredTemplates.length} entries
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredTemplates.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No templates found matching your search' : 'No production templates available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}></th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'id' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('id')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    ID
                    <SortIndicator columnKey="id" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'templatename' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('templatename')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Template Name
                    <SortIndicator columnKey="templatename" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'category' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('category')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Category
                    <SortIndicator columnKey="category" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'itemname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('itemname')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Output Product
                    <SortIndicator columnKey="itemname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'uom' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('uom')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    UOM
                    <SortIndicator columnKey="uom" />
                  </div>
                </th>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''}`}>Raw Materials</th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'status' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('status')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Status
                    <SortIndicator columnKey="status" />
                  </div>
                </th>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((template) => (
                <React.Fragment key={template.id}>
                  <tr
                    className={`UI-table-row ${hoveredRow === template.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                    onMouseEnter={() => setHoveredRow(template.id)}
                    onMouseLeave={() => {
                      setHoveredRow(null);
                      setHoveredAction(null);
                    }}
                  >
                    {/* Expand/Collapse Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <button
                        className={`UI-expand-button ${expandedRows[template.id] ? 'UI-expand-button-expanded' : ''}`}
                        onClick={() => toggleExpandRow(template.id)}
                      >
                        <i className={`fa fa-chevron-${expandedRows[template.id] ? 'up' : 'down'}`} />
                      </button>
                    </td>

                    {/* ID Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {highlightSearchText(template.id, searchTerm)}
                    </td>

                    {/* Template Name Cell */}
                    <td className={`UI-td UI-service-name ${hoveredRow === template.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {highlightSearchText(template.templatename, searchTerm)}
                    </td>

                    {/* Category Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {highlightSearchText(template.category, searchTerm)}
                    </td>

                    {/* Output Product Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {highlightSearchText(template.itemname, searchTerm)}
                    </td>

                    {/* UOM Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {highlightSearchText(template.uom, searchTerm)}
                    </td>

                    {/* Raw Materials Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <span className="UI-raw-materials-count">
                        {template.productiontemplaterawmaterials?.length || 0} items
                      </span>
                    </td>

                    {/* Status Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <span className={`UI-status-badge ${template.status === 'Active' ? 'UI-status-active' : 'UI-status-inactive'}`}>
                        {template.status}
                      </span>
                    </td>

                    {/* Actions Cell */}
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons">
                        {/* Toggle Status */}
                        <Tooltip title={template.status === 'Active' ? 'Deactivate Template' : 'Activate Template'} arrow>
                          <span>
                            <IOSSwitch
                              className="sm"
                              checked={template.status === 'Active'}
                              onChange={() => handleActiveStatusChange(template)}
                              name="checkedB"
                              disabled={template.inuse === "True"}
                            />
                          </span>
                        </Tooltip>

                        {/* Delete Button */}
                        {template.inuse === "False" && (
                          <Tooltip title="Delete Template" arrow>
                            <button
                              className={`UI-action-button UI-delete-button ${hoveredAction === `delete-${template.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                              onClick={() => handleDelete(template)}
                              onMouseEnter={() => setHoveredAction(`delete-${template.id}`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              <i className={`fa fa-trash UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${template.id}` ? 'UI-action-icon-hover' : ''}`} />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row for Raw Materials */}
                  {expandedRows[template.id] && template.productiontemplaterawmaterials && (
                    <tr className={`UI-expanded-row ${isMobile ? 'UI-expanded-row-mobile' : ''}`}>
                      <td colSpan="9">
                        <div className={`UI-raw-materials-details ${isMobile ? 'UI-raw-materials-details-mobile' : ''}`}>
                          <h4>Raw Materials for "{template.templatename}"</h4>
                          <table className={`UI-raw-materials-table ${isMobile ? 'UI-raw-materials-table-mobile' : ''}`}>
                            <thead>
                              <tr>
                                <th>Category</th>
                                <th>Item/Product</th>
                                <th>Quantity</th>
                                <th>UOM</th>
                              </tr>
                            </thead>
                            <tbody>
                              {template.productiontemplaterawmaterials.map((material, index) => (
                                <tr key={index}>
                                  <td>{material.category}</td>
                                  <td>{material.itemname}</td>
                                  <td>{material.quantity}</td>
                                  <td>{material.uom}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
              <nav className={`UI-breadcrumb ${isMobile ? 'UI-breadcrumb-mobile' : ''}`}>
                <Link
                  to="/production"
                  className={`UI-breadcrumb-item ${hovered === 'home' ? 'UI-breadcrumb-item-hover' : ''}`}
                  onMouseEnter={() => setHovered('home')}
                  onMouseLeave={() => setHovered(null)}
                >
                  Home
                </Link>
                <span>/</span>
                <span className="UI-breadcrumb-item UI-breadcrumb-active">
                  Production Template
                </span>
              </nav>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Production Template
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage production templates
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search templates..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New Production Template button */}
                    {(productionid === '2' || productionid === '3') && (
                      <Link
                        to="/newproductiontemplate"
                        state={{
                          id: -1,
                          pagename: "New Production Template",
                        }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Template"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default withRouter(ProductionTemplate);